import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';

const CHANNEL_PRIORITY = {
  'Channel::Email': 1,
  'Channel::Whatsapp': 2,
  'Channel::Sms': 3,
  'Channel::TwilioSms': 4,
  'Channel::WebWidget': 5,
  'Channel::Api': 6,
};

export const generateLabelForContactableInboxesList = ({
  name, email, channelType, phoneNumber,
}) => {
  if (channelType === INBOX_TYPES.EMAIL) return `${name} (${email})`;
  if (channelType === INBOX_TYPES.TWILIO || channelType === INBOX_TYPES.WHATSAPP) {
    return phoneNumber ? `${name} (${phoneNumber})` : name;
  }
  return name;
};

const transformInbox = ({
  name,
  id,
  email,
  channelType,
  phoneNumber,
  medium,
  voiceEnabled,
  ...rest
}) => ({
  id,
  icon: getInboxIconByType(channelType, medium, 'line', voiceEnabled),
  label: generateLabelForContactableInboxesList({
    name,
    email,
    channelType,
    phoneNumber,
  }),
  action: 'inbox',
  value: id,
  name,
  email,
  phoneNumber,
  channelType,
  medium,
  voiceEnabled,
  ...rest,
});

export const compareInboxes = (a, b) => {
  const priorityA = CHANNEL_PRIORITY[a.channelType] || 999;
  const priorityB = CHANNEL_PRIORITY[b.channelType] || 999;
  if (priorityA !== priorityB) return priorityA - priorityB;
  return (a.name || '').localeCompare(b.name || '');
};

export const buildContactableInboxesList = contactInboxes => {
  if (!contactInboxes) return [];
  return contactInboxes.map(transformInbox).sort(compareInboxes);
};

export const getCapitalizedNameFromEmail = email => {
  const name = email.match(/^([^@]*)@/)?.[1] || email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const processContactableInboxes = inboxes => inboxes.map(inbox => ({ ...inbox.inbox, sourceId: inbox.sourceId }));

export const mergeInboxDetails = (inboxesData, inboxesList = []) => {
  if (!inboxesData?.length) return [];
  return inboxesData.map(inboxData => {
    const matchingInbox = inboxesList.find(inbox => inbox.id === inboxData.id) || {};
    return { ...camelcaseKeys(matchingInbox, { deep: true }), ...inboxData };
  });
};

export const prepareAttachmentPayload = (attachedFiles, directUploadsEnabled) => {
  const files = [];
  attachedFiles.forEach(attachment => {
    if (directUploadsEnabled) files.push(attachment.blobSignedId);
    else files.push(attachment.resource.file);
  });
  return files;
};

export const prepareNewMessagePayload = ({
  targetInbox, selectedContact, message, subject, ccEmails, bccEmails,
  currentUser, attachedFiles = [], directUploadsEnabled = false, customAttributes = {},
}) => {
  if (!Number.isFinite(targetInbox?.id)) throw new Error('targetInbox.id is missing');
  if (!Number.isFinite(selectedContact?.id)) throw new Error('selectedContact.id is missing');

  const payload = {
    inbox_id: targetInbox.id,
    source_id: targetInbox.sourceId,
    contact_id: Number(selectedContact.id),
    message: { content: message },
    assignee_id: currentUser.id,
  };

  if (attachedFiles?.length) payload.files = prepareAttachmentPayload(attachedFiles, directUploadsEnabled);
  if (subject) payload.mail_subject = subject;
  if (ccEmails) payload.message.cc_emails = ccEmails;
  if (bccEmails) payload.message.bcc_emails = bccEmails;

  if (customAttributes.title) {
    payload.custom_attributes = { title: customAttributes.title };
  }

  return payload;
};

export const prepareWhatsAppMessagePayload = ({
  targetInbox, selectedContact, message, templateParams, currentUser,
}) => {
  if (!Number.isFinite(targetInbox?.id) || !Number.isFinite(selectedContact?.id)) {
    throw new Error('Invalid inbox or contact ID');
  }
  return {
    inbox_id: targetInbox.id,
    source_id: targetInbox.sourceId,
    contact_id: selectedContact.id,
    message: { content: message, template_params: templateParams },
    assignee_id: currentUser.id,
  };
};

// API Calls
const MIN_SEARCH_LENGTH = 2;

export const createContactSearcher = () => {
  let controller = null;
  return async (query, { skipMinLength = false, reachableOnly = true } = {}) => {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    controller?.abort();
    if (!trimmed || (!skipMinLength && trimmed.length < MIN_SEARCH_LENGTH)) return [];
    controller = new AbortController();
    const { signal } = controller;
    try {
      const { data: { payload } } = await ContactAPI.search(trimmed, 1, 'name', '', { signal });
      const camelCasedPayload = camelcaseKeys(payload, { deep: true });
      if (!reachableOnly) return camelCasedPayload || [];
      return camelCasedPayload?.filter(c => c.phoneNumber || c.email) || [];
    } catch (error) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') return null;
      throw error;
    }
  };
};

export const createNewContact = async input => {
  const payload = {
    name: input.startsWith('+') ? input.slice(1) : getCapitalizedNameFromEmail(input),
    ...(input.startsWith('+') ? { phone_number: input } : { email: input }),
  };
  const { data: { payload: { contact: newContact } } } = await ContactAPI.create(payload);
  return camelcaseKeys(newContact, { deep: true });
};

export const fetchContactableInboxes = async contactId => {
  const { data: { payload: inboxes = [] } } = await ContactAPI.getContactableInboxes(contactId);
  return processContactableInboxes(camelcaseKeys(inboxes, { deep: true }));
};
